import type { ReactNode } from "react";

type PageWidth = "compact" | "default" | "wide" | "workspace" | "full";

const widthClasses: Record<PageWidth, string> = {
  compact: "max-w-4xl",
  default: "max-w-5xl",
  wide: "max-w-6xl",
  workspace: "max-w-[2200px]",
  full: "max-w-none",
};

interface AppPageProps {
  children: ReactNode;
  width?: PageWidth;
  className?: string;
}

export function AppPage({ children, width = "wide", className = "" }: AppPageProps) {
  return <div className={`mx-auto w-full ${widthClasses[width]} ${className}`}>{children}</div>;
}

interface AppPageHeaderProps {
  title: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
  meta?: ReactNode;
  className?: string;
}

export function AppPageHeader({
  title,
  description,
  actions,
  meta,
  className = "",
}: AppPageHeaderProps) {
  return (
    <header className={`mb-4 flex min-w-0 items-start justify-between gap-3 sm:mb-6 ${className}`}>
      <div className="min-w-0 flex-1">
        <div className="flex min-w-0 flex-wrap items-center gap-2.5">
          <h1 className="text-[20px] font-semibold leading-tight tracking-[-.01em] text-on-surface">
            {title}
          </h1>
          {meta}
        </div>
        {description && (
          <div className="mt-1.5 hidden max-w-3xl text-[13px] leading-relaxed text-on-surface-variant sm:block">
            {description}
          </div>
        )}
      </div>
      {actions && (
        <div className="flex max-w-[58%] shrink-0 flex-wrap items-center justify-end gap-2 sm:max-w-none">
          {actions}
        </div>
      )}
    </header>
  );
}

interface AppSectionHeaderProps {
  title: ReactNode;
  description?: ReactNode;
  action?: ReactNode;
  className?: string;
}

export function AppSectionHeader({
  title,
  description,
  action,
  className = "",
}: AppSectionHeaderProps) {
  return (
    <div className={`flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between ${className}`}>
      <div className="min-w-0">
        <h2 className="text-sm font-semibold text-on-surface">{title}</h2>
        {description && (
          <p className="mt-1 max-w-2xl text-xs leading-relaxed text-on-surface-variant">
            {description}
          </p>
        )}
      </div>
      {action && <div className="shrink-0">{action}</div>}
    </div>
  );
}

interface AppEmptyStateProps {
  icon: string;
  title: ReactNode;
  description?: ReactNode;
  primaryAction?: ReactNode;
  secondaryAction?: ReactNode;
  compact?: boolean;
  className?: string;
}

export function AppEmptyState({
  icon,
  title,
  description,
  primaryAction,
  secondaryAction,
  compact = false,
  className = "",
}: AppEmptyStateProps) {
  return (
    <div
      className={`flex flex-col items-center justify-center rounded-[13px] border border-dashed border-outline bg-surface-container-low/50 px-5 text-center ${
        compact ? "min-h-48 py-8" : "min-h-64 py-10"
      } ${className}`}
    >
      <span
        className="material-symbols-outlined mb-4 inline-flex h-11 w-11 items-center justify-center rounded-xl bg-on-surface/[.06] text-[24px] text-on-surface-variant"
        aria-hidden="true"
      >
        {icon}
      </span>
      <h2 className="text-sm font-semibold text-on-surface">{title}</h2>
      {description && (
        <div className="mt-1.5 max-w-lg text-[13px] leading-relaxed text-on-surface-variant">
          {description}
        </div>
      )}
      {(primaryAction || secondaryAction) && (
        <div className="mt-5 flex flex-wrap items-center justify-center gap-2">
          {primaryAction}
          {secondaryAction}
        </div>
      )}
    </div>
  );
}
